import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Clock, Package, Building2, Server, Cable, Users, Layers } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const ENTITY_ICONS = {
  product: Package,
  vendor: Building2,
  platform: Server,
  integration: Cable,
  property_group: Users,
  assignment: Layers,
};
const ACTION_COLORS = {
  created: 'bg-emerald-100 text-emerald-700',
  updated: 'bg-blue-100 text-blue-700',
  deleted: 'bg-red-100 text-red-700',
  merged: 'bg-amber-100 text-amber-700',
};

export default function ActivityLog({ token }) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [page, setPage] = useState(0);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 30, skip: page * 30 };
    if (filterType !== 'all') params.entity_type = filterType;
    axios.get(`${BACKEND_URL}/api/activity-log`, { headers, params })
      .then(res => { setEntries(res.data.entries || []); setTotal(res.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [headers, filterType, page]);

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4" data-testid="activity-log">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Activity Log</h2>
          <p className="text-sm text-muted-foreground">{total} recorded activities</p>
        </div>
        <Select value={filterType} onValueChange={v => { setFilterType(v); setPage(0); }}>
          <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="product">Products</SelectItem>
            <SelectItem value="vendor">Vendors</SelectItem>
            <SelectItem value="property_group">Groups</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-12 text-center">
          <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mx-auto" />
        </div>
      ) : entries.length === 0 ? (
        <div className="py-12 text-center">
          <Clock className="h-8 w-8 text-muted-foreground/25 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No activity recorded yet. Actions will appear here as changes are made.</p>
        </div>
      ) : (
        <div className="space-y-1" data-testid="activity-entries">
          {entries.map(e => {
            const Icon = ENTITY_ICONS[e.entity_type] || Clock;
            const actionColor = ACTION_COLORS[e.action] || 'bg-gray-100 text-gray-600';
            return (
              <div key={e.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/30 transition-colors">
                <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm">
                    <Badge className={`text-[10px] mr-1.5 ${actionColor}`}>{e.action}</Badge>
                    <span className="font-medium">{e.entity_name}</span>
                  </span>
                  {e.details && <span className="text-xs text-muted-foreground ml-1">— {e.details}</span>}
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0">{formatTime(e.timestamp)}</span>
              </div>
            );
          })}
        </div>
      )}

      {total > 30 && (
        <div className="flex justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <Button size="sm" variant="outline" disabled={(page + 1) * 30 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
